import { useState, useEffect, useContext} from 'react'
import { Link, useNavigate } from 'react-router-dom'
import TopNavBar from '../components/navigation/TopNavBar'
import RoundedButtonLink from '../components/buttons/RoundedButtonLink'
import EmptyListView from '../components/views/EmptyListView'
import TwoButtonsModal from '../components/modals/TwoButtonsModal'
import LoadingModal from '../components/modals/LoadingModal'
import BoatCard from '../components/cards/BoatCard'
import * as Constants from '../utils/constants'
import { UserContext } from '../App'
import { isExpired } from 'react-jwt'
import { ToastContainer, toast } from 'react-toastify'
import noBoats from '../assets/no-boats.png'
import strings from '../res/strings'
import 'react-toastify/dist/ReactToastify.css'

const Boats = () => {
    const [showRemoveBoatModal, setShowRemoveBoatModal] = useState(false)
    const [boats, setBoats] = useState([])
    const [selectedBoat, setSelectedBoat] = useState(null)
    const [dropdowns, setDropdowns] = useState(null)

    const [isLoading, setIsLoading] = useState(true)

    const { loggedUser, setLoggedUser } = useContext(UserContext)
    const navigate = useNavigate()

    useEffect(() => {
        if(loggedUser !== null && loggedUser !== 'refresh') {
            const isTokenExpired = isExpired(loggedUser.jwtToken)

            if(isTokenExpired) {
                setLoggedUser(null)
                navigate('/')
            } else if(loggedUser.role !== Constants.Roles.CLIENT) {
                navigate('/')
            } else {
                getDropdowns()
                getBoats()
            }
        } else if (loggedUser === null) {
            navigate('/')
        }
    }, [loggedUser])

    // Handle get dropdowns request
    const getDropdowns = () => {
        fetch('/api/Boat/Dropdowns', {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            if (response.status === 200) {
                const jsonRes = await response.json()

                setDropdowns(jsonRes)
            } else {
                setTimeout(() => {
                    setIsLoading(false)
                    showErrorToast(strings.getDropdownsError)

                    setDropdowns([])
                }, 1000)   
            }
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                setDropdowns([])
            }, 1000)
        })
    }

    // Handle get boats request
    const getBoats = () => {
        fetch('/api/Boat/List', {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)

                if (response.status === 200) {
                    const jsonRes = await response.json()
                  
                    setBoats(jsonRes)
                } else {
                    showErrorToast(strings.getBoatsError)
                    setBoats([])
                }
            }, 1000)

        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)
                
                setBoats([])
            }, 1000)
        })
    }

    // Show remove modal
    const onClickRemove = (event, boat) => {
        event.stopPropagation()
        setSelectedBoat(boat)
        setShowRemoveBoatModal(true)
    }

    // Handles the removal of a boat
    const removeBoat = () => {
        setShowRemoveBoatModal(false)
        setIsLoading(true)

        // Remove boat
        fetch(`/api/Boat/${selectedBoat.id}/Delete`, {
            method:'DELETE',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(() => {
                if (response.status === 200) {
                    getBoats()
                } else {
                    showErrorToast(strings.deleteBoatError)
                }
            }, 1000)

        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)
            }, 1000)  
        })
    }

    // Handle show error toast
    const showErrorToast = (error) => {
        toast.error(error, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'colored'
        })
    }

    return (
        <>
            <TopNavBar />

            <div className='h-14 border-b border-accent flex flex-row items-center px-[12.5rem] space-x-9'>
                <Link to='/schedules' className='font-montserrat text-neutral5 cursor-pointer'>{strings.schedules}</Link>
                <p className='font-montserrat font-semibold text-accent'>{strings.boats}</p>
            </div>

            <div className='px-[12.5rem] min-h-screen mb-6'>
                { isLoading ?
                    <LoadingModal />
                :   <>
                        { boats.length === 0 ? null :
                            <div className='flex items-end justify-between mt-12'>
                                <p className='font-montserrat font-semibold'>{strings.yourBoats}</p>

                                <div className='flex flex w-[14.625rem]'>
                                    <RoundedButtonLink 
                                        path='/new-boat'
                                        text={strings.addBoat}
                                        height={40}
                                        bgColor='#007EB4'
                                        flex={true}
                                    />
                                </div>

                            </div>
                        }

                        { boats.length > 0 ?
                            <div className='grid grid-cols-3 gap-6 my-6'>

                                { boats.map(boat => (
                                    <BoatCard
                                        key={boat.id}
                                        item={boat} 
                                        onPressCard={() => navigate(`/boat-details/${boat.id}`)}
                                        onPressRemove={(event) => onClickRemove(event, boat)}
                                        dropdowns={dropdowns}
                                    />
                                )) }

                            </div>
                        :   <EmptyListView 
                                image={noBoats}
                                message={strings.noBoats}
                                path='/new-boat'
                                buttonText={strings.addBoat}
                            />
                        }
                    </>
                }

            </div>

            <TwoButtonsModal 
                open={showRemoveBoatModal}
                onClose={() => setShowRemoveBoatModal(false)}
                title={strings.removeBoat}
                message={strings.removeBoatMessage}
                buttonText1={strings.keep}
                buttonText2={strings.removeBoatTitle}
                onClick={removeBoat}
            />

            <ToastContainer />
        </>
    )
}

export default Boats